// ============================================================================
// SpellPassives.js - Challenger Spell Logic
// ============================================================================
// NOTE: Helper functions (applyBuff, applyHealing, etc.) are INJECTED into execute()
// to avoid circular dependencies with CombatEngine.js.
// ============================================================================

// We can safely duplicate these simple string constants or import from a constants file if it existed.
// For now, hardcoding them here is safe and clean enough to avoid more files.
const DamageType = {
    PHYSICAL: 'Physical',
    MAGIC: 'Magic',
    TRUE: 'True',
};

const BuffType = {
    STAT_FLAT: 'STAT_FLAT',
    STAT_PCT: 'STAT_PCT',
    DAMAGE_AMP: 'DAMAGE_AMP',
    INVULNERABLE: 'INVULNERABLE',
    CC: 'CC',
};

const CCType = {
    SLOW: 'Slow',
    STUN: 'Stun',
};

export const CHALLENGER_SPELLS = {

    'Execute': {
        name: 'Execute',
        cooldown: 90,
        description: 'Deals true damage equal to 16% of the target\'s lost HP.',
        castTime: 0,
        // Context: { applyBuff, applyHealing, calculateDamage, etc. }
        execute(actor, target, tick, log, ctx) {
            const lostHP = target.maxHP - target.currentHP;
            const damage = Math.floor(lostHP * 0.16);

            const damageSource = {
                name: 'Execute',
                baseDamage: damage,
                damageType: DamageType.TRUE,
                canCrit: false,
            };

            return damageSource; // Return source, engine calculates final result
        }
    },

    'Roar': {
        name: 'Roar',
        cooldown: 60,
        description: 'Increases damage dealt by 10% and restores 2% HP/sec for 6 seconds.',
        castTime: 0,
        execute(actor, target, tick, log, ctx) {
            if (ctx.applyBuff) {
                // Damage Amp
                ctx.applyBuff(actor, {
                    type: BuffType.DAMAGE_AMP,
                    value: 0.10,
                    name: 'Roar Amp',
                    source: 'Roar',
                    duration: 6,
                }, tick, log);

                // Healing (Hot) - Optional, mimicking "Enraged" sustainability
                // Let's stick to core mechanic first.
            }
            return null;
        }
    },

    'Sprint': {
        name: 'Sprint',
        cooldown: 100,
        description: 'Running speed increased by 70% (decays) for 10s. Immune to slow effects.',
        castTime: 0,
        execute(actor, target, tick, log, ctx) {
            if (ctx.applyBuff) {
                ctx.applyBuff(actor, {
                    type: BuffType.STAT_PCT,
                    stat: 'movement_speed',
                    value: 0.70,
                    name: 'Sprint',
                    source: 'Sprint',
                    duration: 10,
                }, tick, log);
            }
            return null;
        }
    },

    'Purify': {
        name: 'Purify',
        cooldown: 120,
        description: 'Removes all CC and grants CC immunity for 1.5s.',
        castTime: 0,
        execute(actor, target, tick, log, ctx) {
            // Manual cleanup if context provides direct access or we assume engine handles "INVULNERABLE" as CC immune
            if (ctx.applyBuff) {
                ctx.applyBuff(actor, {
                    type: BuffType.INVULNERABLE, // Using Invulnerable as proxy for CC Immune in this simple engine
                    name: 'Purify',
                    source: 'Purify',
                    duration: 1.5,
                }, tick, log);
            }
            return null;
        }
    },

    'Flicker': {
        name: 'Flicker',
        cooldown: 120,
        description: 'Teleport short distance.',
        castTime: 0,
        execute(actor, target, tick, log, ctx) {
            log.addEvent(tick, 'SPELL_USE', {
                hero: actor.heroName,
                spell: 'Flicker',
                detail: 'Teleported',
            });
            return null;
        }
    },

    'Punish': {
        name: 'Punish',
        cooldown: 30,
        description: 'Deals 800 true damage (Scaling) to heroes and slows them.',
        castTime: 0,
        execute(actor, target, tick, log, ctx) {
            const dmg = 800 + (actor.level * 60); // Simplified formula

            // Apply Slow
            if (ctx.applyBuff) {
                ctx.applyBuff(target, {
                    type: BuffType.CC,
                    ccType: CCType.SLOW,
                    value: 0.50,
                    name: 'Frostbite Slow',
                    source: 'Punish',
                    duration: 2,
                }, tick, log);
            }

            return {
                name: 'Punish',
                baseDamage: dmg,
                damageType: DamageType.TRUE,
                canCrit: false,
            };
        }
    },

    'Heal': {
        name: 'Heal',
        cooldown: 90,
        description: 'Restores 15% HP and increases Move Speed.',
        castTime: 0,
        execute(actor, target, tick, log, ctx) {
            const healAmt = actor.maxHP * 0.15;
            if (ctx.applyHealing) {
                ctx.applyHealing(actor, healAmt, log, tick, 'Heal Spell');
            }

            if (ctx.applyBuff) {
                ctx.applyBuff(actor, {
                    type: BuffType.STAT_PCT,
                    stat: 'movement_speed',
                    value: 0.15,
                    name: 'Heal Speed',
                    source: 'Heal',
                    duration: 2,
                }, tick, log);
            }
            return null;
        }
    },

    'Daze': {
        name: 'Daze',
        cooldown: 90,
        description: 'Stun enemies around for 0.75s.',
        castTime: 0,
        execute(actor, target, tick, log, ctx) {
            if (ctx.applyCC) {
                ctx.applyCC(target, {
                    ccType: CCType.STUN,
                    duration: 0.75,
                }, tick, log, 'Daze');
            }
            return null;
        }
    }
};
